import { extractSectionFromChapterSectionHeading } from '@shared/ast/extractChapterSection'
import { Dispatch, SetStateAction, useCallback, useState } from 'react'
import type { MouseEvent } from 'react'
import toast from 'react-hot-toast'

import type { PublishTarget } from '../../../components/publishing/PublishingModal'
import { ContentFile, apiService } from '../../../services/api'
import {
  publishChapter,
  publishChapterSection,
  unpublishChapter,
  unpublishChapterSection,
} from '../../../services/publishService'
import { ContentAST } from '../../../transforms/ast'
import { hasMarkedSectionsInAST } from '../../../utils/astContentMarking'
import { logger } from '../../../utils/logger'

interface UsePublishingActionsArgs {
  bookId?: string
  currentChapter: ContentFile | null
  currentContent: ContentAST | null
  hasUnsavedChanges: boolean
  setHasUnsavedChanges: Dispatch<SetStateAction<boolean>>
  setChapters: Dispatch<SetStateAction<ContentFile[]>>
  setCurrentChapter: Dispatch<SetStateAction<ContentFile | null>>
  setPublishOpen: Dispatch<SetStateAction<boolean>>
  setPublishTarget: Dispatch<SetStateAction<PublishTarget | null>>
  handleChapterSelect: (chapter: ContentFile) => Promise<void>
}

export function usePublishingActions({
  bookId,
  currentChapter,
  currentContent,
  hasUnsavedChanges,
  setHasUnsavedChanges,
  setChapters,
  setCurrentChapter,
  setPublishOpen,
  setPublishTarget,
  handleChapterSelect,
}: UsePublishingActionsArgs) {
  /** `${sourceFilename}:${headingBlockId}` while a chapter-section action is in flight */
  const [chapterSectionPublishBusyKey, setChapterSectionPublishBusyKey] = useState<string | null>(null)

  const refreshChapterState = useCallback(
    async (chapterId: string) => {
      if (!bookId) {
        return
      }
      const updatedChapter = await apiService.getChapter(bookId, chapterId)
      const next = updatedChapter.data

      setChapters((prev) =>
        prev.map((ch) => {
          if (ch.filename !== chapterId) {
            return ch
          }
          return {
            ...next,
            tokenCount: next.tokenCount ?? ch.tokenCount,
          }
        }),
      )
      if (currentChapter?.filename === chapterId) {
        setCurrentChapter((prev) => {
          if (!prev) {
            return prev
          }
          return next
        })
      }
    },
    [bookId, currentChapter?.filename, setChapters, setCurrentChapter],
  )

  const handleChapterPublish = useCallback(
    async (chapter: ContentFile) => {
      if (!bookId) {
        return
      }

      const isPublished = Boolean(chapter.published)

      if (isPublished) {
        try {
          const result = await unpublishChapter(bookId, chapter.filename)
          if (result.success) {
            await refreshChapterState(chapter.filename)
          } else {
            toast.error(result.message || 'Failed to unpublish chapter')
          }
        } catch (err: any) {
          logger.error('Failed to unpublish chapter:', err)
          toast.error(err?.message || 'Failed to unpublish chapter')
        }
        return
      }

      if (currentChapter?.filename === chapter.filename && hasUnsavedChanges && currentContent) {
        try {
          await apiService.updateChapter(bookId, chapter.filename, {
            authorContent: currentContent,
          })
          setHasUnsavedChanges(false)
        } catch (err) {
          logger.error('Failed to save chapter before publishing:', err)
          toast.error('Failed to save chapter. Please save manually before publishing.')
          return
        }
      }

      const chapterContent =
        currentChapter?.filename === chapter.filename && currentContent ? currentContent : chapter.authorContent

      if (!chapterContent) {
        toast.error('Chapter has no content to publish')
        return
      }

      const hasMarked = hasMarkedSectionsInAST(chapterContent)
      if (!hasMarked) {
        try {
          const result = await publishChapter(bookId, chapter.filename, chapterContent, {})
          if (result.success) {
            await refreshChapterState(chapter.filename)
          } else {
            toast.error(result.message || 'Failed to publish chapter')
          }
        } catch (err: any) {
          logger.error('Failed to publish chapter:', err)
          toast.error(err?.message || 'Failed to publish chapter')
        }
        return
      }

      if (currentChapter?.filename !== chapter.filename) {
        await handleChapterSelect(chapter)
      }
      setPublishTarget({ kind: 'whole', filename: chapter.filename, authorContent: chapterContent })
      setPublishOpen(true)
    },
    [
      bookId,
      currentChapter?.filename,
      currentContent,
      handleChapterSelect,
      hasUnsavedChanges,
      refreshChapterState,
      setHasUnsavedChanges,
      setPublishOpen,
      setPublishTarget,
    ],
  )

  const handleChapterSectionPublish = useCallback(
    async (sourceChapter: ContentFile, headingBlockId: string, e?: MouseEvent) => {
      if (!bookId) {
        return
      }
      const shiftKey = Boolean(e?.shiftKey)
      const actionKey = `${sourceChapter.filename}:${headingBlockId}`
      setChapterSectionPublishBusyKey(actionKey)

      try {
        if (currentChapter?.filename === sourceChapter.filename && hasUnsavedChanges && currentContent) {
          try {
            await apiService.updateChapter(bookId, sourceChapter.filename, {
              authorContent: currentContent,
            })
            setHasUnsavedChanges(false)
          } catch (err) {
            logger.error('Failed to save chapter before publishing chapter section:', err)
            toast.error('Failed to save chapter. Save manually before publishing this section.')
            return
          }
        }

        // Load the latest source file to extract the slice for this ^ section.
        let fullSource: ContentFile | undefined
        try {
          const res = await apiService.getChapter(bookId, sourceChapter.filename)
          if (res.success && res.data) {
            fullSource = res.data
          }
        } catch (err) {
          logger.error('Failed to load source file before chapter-section publish:', err)
        }
        if (!fullSource?.authorContent) {
          toast.error('Could not load source file')
          return
        }

        const sourceAuthorAST = fullSource.authorContent
        const extracted = extractSectionFromChapterSectionHeading(sourceAuthorAST, headingBlockId)
        if (!extracted) {
          toast.error('Could not locate chapter section in source file')
          return
        }
        const sliceAuthorAST = extracted.slice

        const isPublished = (fullSource.publishedSections ?? []).includes(headingBlockId)

        // Click on published section = unpublish. Shift+click = re-publish (overwrite).
        if (isPublished && !shiftKey) {
          try {
            const result = await unpublishChapterSection(bookId, sourceChapter.filename, headingBlockId)
            if (result.success) {
              await refreshChapterState(sourceChapter.filename)
              toast.success('Chapter section unpublished')
            } else {
              toast.error(result.message || 'Failed to unpublish')
            }
          } catch (err: any) {
            logger.error('Failed to unpublish chapter section:', err)
            toast.error(err?.message || 'Failed to unpublish')
          }
          return
        }

        // Publish (or re-publish on shift+click). If the slice has marked sections,
        // open the dialog for the master password.
        if (hasMarkedSectionsInAST(sliceAuthorAST)) {
          setPublishTarget({
            kind: 'section',
            sourceFilename: sourceChapter.filename,
            headingBlockId,
            sourceAuthorAST,
            sliceAuthorAST,
          })
          setPublishOpen(true)
          return
        }

        try {
          const result = await publishChapterSection(
            bookId,
            sourceChapter.filename,
            headingBlockId,
            sourceAuthorAST,
            {},
          )
          if (result.success) {
            await refreshChapterState(sourceChapter.filename)
            toast.success(isPublished ? 'Chapter section updated' : 'Chapter section published')
          } else {
            toast.error(result.message || 'Failed to publish')
          }
        } catch (err: any) {
          logger.error('Failed to publish chapter section:', err)
          toast.error(err?.message || 'Failed to publish')
        }
      } finally {
        setChapterSectionPublishBusyKey(null)
      }
    },
    [
      bookId,
      currentChapter?.filename,
      currentContent,
      hasUnsavedChanges,
      refreshChapterState,
      setHasUnsavedChanges,
      setPublishOpen,
      setPublishTarget,
    ],
  )

  return {
    refreshChapterState,
    handleChapterPublish,
    handleChapterSectionPublish,
    chapterSectionPublishBusyKey,
  }
}
